/**
 * Whose progress is this?
 *
 * The learn UI remembers things on the device — a quiz commitment, a prediction
 * run — so a learner is not re-gated every visit. Those keys were global, which
 * on a shared browser meant the SECOND person to sign in inherited the first
 * one's state: gates already open, "you stepped this — 2 of 2 correct" over a
 * run they never made, a recap quoting somebody else's answers.
 *
 * Wrong on two counts. It is incorrect, and it leaks one learner's work to
 * another on any shared machine — a lab, a library, a family laptop, which is
 * most of the machines this course will be read on.
 *
 * Every key is now namespaced by the signed-in user. Signing out and back in as
 * someone else changes the namespace, so the previous learner's records are
 * simply not addressable rather than being merged or wiped.
 *
 * Still device-local: the same learner on a different machine starts fresh.
 * That is a real limit of this whole mechanism, not something scoping fixes —
 * moving it server-side is the answer, and this is not that.
 *
 * `anon` is a deliberate bucket rather than a refusal to store. Auth resolves
 * asynchronously, so the first paint has no user; writing under `anon` and
 * re-reading when the scope lands beats losing the write.
 */
import { useEffect, useState } from 'react';

let current = null;
const listeners = new Set();

/** Called by AuthProvider whenever the session changes. */
export function setLearnerScope(id) {
  const next = id || null;
  if (next === current) return;
  current = next;
  listeners.forEach((fn) => {
    try { fn(current); } catch (_) { /* a bad listener must not break auth */ }
  });
}

export function getLearnerScope() {
  return current;
}

export function subscribeLearnerScope(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** `f2c:<user>:<ns>:<id>` — the only place a learn-storage key is built. */
export function scopedKey(ns, id) {
  return `f2c:${current || 'anon'}:${ns}:${id}`;
}

/**
 * Re-render when the signed-in learner changes.
 *
 * Anything reading scoped storage needs this in its deps: auth resolves after
 * first paint, so a component that read once at mount would show the `anon`
 * bucket for ever and never notice the real user arriving.
 */
export function useLearnerScope() {
  const [scope, setScope] = useState(current);
  useEffect(() => subscribeLearnerScope(setScope), []);
  return scope;
}

/**
 * Drop the pre-scoping keys, once.
 *
 * They were shared between everyone who used this browser. Leaving them would
 * leave one learner's answers readable in another's devtools, and they can
 * never be read by the app again anyway.
 */
export function purgeLegacyLearnKeys() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('f2c:quiz:') || k.startsWith('f2c:predict:'))) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    /* storage unavailable — nothing to purge */
  }
}
